/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { Suspense, useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router'
import { activityApiService } from '~/services/api/api.activity'
import { useTranslation } from 'react-i18next'
import { RcmTableDataWrapper } from '~/components/rcm-table'
import { baseApiService } from '~/services/api/base'
import RcmTableFilterTrigger from '~/components/rcm-table/RcmTableFilterTrigger'
import TableFilter from './TableFilter'
import LoadingSpinner from '~/components/LoadingSpinner'
import FsiActivityTable from './Table'
import { SystemFolderType } from '~/constants/fsi'
import { DocumentsActivityPageMenu } from '~/screens/reports/activity/DocumentsActivityPageMenu'
import ModalInfo from '~/components/ModalInfo'

interface FileItem {
  id?: number
  name?: string
  documentUID?: number
  path?: Array<{ id?: number; name?: string }>
  systemFolderType?: string
  hasNoACLToRCRoot?: boolean
  obsolete?: boolean
}

/** Convert a Date object or ISO date string (from JSON deserialization) to epoch ms. */
function toEpoch(value: unknown): number | undefined {
  if (value == null) return undefined
  const d = value instanceof Date ? value : new Date(value as string)
  return isNaN(d.getTime()) ? undefined : d.getTime()
}

const FsiActivity: React.FC = () => {
  const persistenceKey = 'fsiActivity'
  const { t: translate } = useTranslation()
  const { id } = useParams()
  const fsiId = id ? parseInt(id) : 1
  const [currentFolder, setCurrentFolder] = useState<FileItem | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isFormSubmitted, setIsFormSubmitted] = useState(false)
  const [isReportExport, setIsReportExport] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const filterFormRef = useRef<any>(null)
  const currentFilterRef = useRef<any>(null)

  useEffect(() => {
    if (fsiId) {
      setIsLoading(true)
      // Call getFile with pageSize=1 to get complete file info including documentUID and path
      baseApiService
        .fetch<any>(`/api/files/${fsiId}`, {
          params: { pageSize: 1 }
        })
        .then((data: any) => {
          const folder = {
            ...data,
            name: data.name === '/' ? translate('BROWSER_ROOT_FOLDER_NAME') : data.name
          }
          setCurrentFolder(folder)
          setIsLoading(false)
        })
        .catch((error: any) => {
          if (error?.status === 403 && fsiId === 1) {
            setCurrentFolder({
              name: translate('BROWSER_ROOT_FOLDER_NAME'),
              hasNoACLToRCRoot: true
            })
          }
          setIsLoading(false)
        })
    }
  }, [fsiId, translate])

  const loadData = async (queryParams: any, filter: any) => {
    // Track current filter for export
    currentFilterRef.current = filter

    // Only load if we have currentFolder and documentUID
    if (!currentFolder || !currentFolder.documentUID) {
      return {
        elements: [],
        totalElements: 0,
        hasNextPage: false
      }
    }

    const params: any = { ...queryParams }
    params.docuId = currentFolder.documentUID

    // Filter already contains arrays of values (converted in TableFilter)
    if (filter?.actions && filter.actions.length > 0) {
      params.action = filter.actions
    }

    if (filter?.users && filter.users.length > 0) {
      params.createdById = filter.users
    }

    if (filter?.includeSystemGeneratedEvents !== undefined) {
      params.includeSystemGeneratedEvents = filter.includeSystemGeneratedEvents
    }

    const fromMs = toEpoch(filter?.from)
    if (fromMs !== undefined) {
      params.createdAtFrom = fromMs
    }

    const toMs = toEpoch(filter?.to)
    if (toMs !== undefined) {
      params.createdAtTo = toMs
    }

    const result = await activityApiService.getAllAuditLogs(params)
    return {
      elements: result.elements || [],
      totalElements: result.totalElements ?? 0,
      hasNextPage: result.hasNextPage
    }
  }

  // Handle export when triggered from DocumentsActivityPageMenu
  useEffect(() => {
    if (!isReportExport) return

    // Read live form values directly from Formik ref (not stale currentFilterRef)
    const formValues = filterFormRef.current?.values?.filter
    const filter = formValues || currentFilterRef.current || {}
    const exportParams: any = {
      includeSystemGeneratedEvents: filter?.includeSystemGeneratedEvents ?? false
    }

    if (currentFolder?.documentUID) {
      exportParams.docuId = currentFolder.documentUID
    }

    if (filter?.actions?.length > 0) {
      exportParams.action = filter.actions
    }
    if (filter?.users?.length > 0) {
      exportParams.createdById = filter.users
    }
    const fromMs = toEpoch(filter?.from)
    if (fromMs !== undefined) {
      exportParams.createdAtFrom = fromMs
    }
    const toMs = toEpoch(filter?.to)
    if (toMs !== undefined) {
      exportParams.createdAtTo = toMs
    }

    setShowExportModal(true)
    activityApiService.exportAllAuditlogs(exportParams).catch((err) => {
      console.error('Document Activity Export failed', err)
    })

    setIsReportExport(false)
    setIsFormSubmitted(false)
  }, [isReportExport, currentFolder])

  if (isLoading) {
    return (
      <div className="mcc-content-frame theme-sandman">
        <div className="system-container">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  return (
    <div className="mcc-content-frame theme-sandman">
      <div className="system-container fsi-activity-page">
        <Suspense fallback={<LoadingSpinner />}>
          <RcmTableDataWrapper
            loadData={loadData}
            defaultSortBy={'createdAt'}
            defaultSortDesc={true}
            persistenceKey={persistenceKey}
            doPreload={true}
          >
            <div className="sub-page-header">
              <div className="d-flex flex-wrap">
                <div className="align-left d-flex flex-column align-items-baseline gap-1">
                  <h1>{translate('BROWSER_FSI_ACTIVITY_TITLE')}</h1>

                  {/* Breadcrumb */}
                  {currentFolder && (
                    <div className="breadcrumbs-path">
                      {/* Path segments */}
                      {currentFolder.path?.map((pathItem, index) => (
                        <span key={pathItem.id || index}>
                          {currentFolder.systemFolderType === SystemFolderType.WORKFLOWS ? (
                            // For WORKFLOWS, show path as plain text
                            <span className="current">{pathItem.name}&nbsp;/</span>
                          ) : (
                            // For other types, show as clickable link
                            <span>
                              <Link to={`/browser/${pathItem.id}`}>{pathItem.name}</Link>
                              &nbsp;/
                            </span>
                          )}
                        </span>
                      ))}

                      {/* Current folder/file name */}
                      {!currentFolder.hasNoACLToRCRoot && (
                        <Link to={`/show-file/${currentFolder.id}`}>
                          <span>{currentFolder.name}</span>
                          &nbsp;
                          {currentFolder.obsolete && <b className="obsolete-text-color">{translate('OBSOLETE')}</b>}
                        </Link>
                      )}
                    </div>
                  )}
                </div>
                <div className="align-top d-flex">
                  {currentFolder && (
                    <DocumentsActivityPageMenu
                      fsi={currentFolder}
                      options={{ hideActivity: true }}
                      setIsFormSubmitted={setIsFormSubmitted}
                      setIsReportExport={setIsReportExport}
                    />
                  )}
                  <RcmTableFilterTrigger />
                </div>
              </div>
            </div>

            <div>
              <TableFilter fsiId={fsiId} formRef={filterFormRef} />
              <FsiActivityTable />
            </div>
          </RcmTableDataWrapper>
          {showExportModal && (
            <ModalInfo
              titleMsg="EXPORT_IN_PROCCESS_TITLE"
              msg="REPORTS_EXPORT_BEING_GENERATED_MSG_SUCCESS"
              modalStatus={() => setShowExportModal(false)}
            />
          )}
        </Suspense>
      </div>
    </div>
  )
}

export default FsiActivity
